import { useEffect, useState } from 'react';
import { X, Lock, ShieldCheck, Eye, EyeOff } from 'lucide-react';
import { api, setPinUnlockToken } from '../lib/api';

// Regex reused by frontend and backend: 4-8 printable non-space ASCII chars.
const PIN_REGEX = /^[\x21-\x7E]{4,8}$/;

/**
 * Small input with an inline eye toggle. Kept local so we don't spawn
 * yet-another-shadcn-wrapper file for a two-usage pattern.
 */
function PinInput({ value, onChange, placeholder, testId, autoFocus, onEnter }) {
  const [reveal, setReveal] = useState(false);
  return (
    <div className="relative mb-3">
      <input data-testid={testId} autoFocus={autoFocus} value={value}
        onChange={(e) => onChange(e.target.value.slice(0, 8))}
        onKeyDown={(e) => { if (e.key === 'Enter' && onEnter) onEnter(); }}
        type={reveal ? 'text' : 'password'} inputMode="text"
        autoComplete={placeholder?.includes('New') ? 'new-password' : 'current-password'}
        className="w-full pl-4 pr-12 py-3 rounded-2xl border-2 border-slate-200 focus:border-[#FF6B1A] outline-none text-lg tracking-widest text-center"
        placeholder={placeholder}/>
      <button type="button" data-testid={`${testId}-reveal`} onClick={() => setReveal((r) => !r)}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-slate-400 hover:text-slate-700"
        aria-label={reveal ? 'Hide PIN' : 'Show PIN'}>
        {reveal ? <EyeOff size={18} strokeWidth={2.5}/> : <Eye size={18} strokeWidth={2.5}/>}
      </button>
    </div>
  );
}

/**
 * Modal that gates the "Add new book" / camera-capture flow.
 *
 * Behaviour:
 *   • On mount, checks /parent/pin/status
 *   • If no PIN set → shows "Set your PIN" form (4-8 alphanumeric)
 *   • If PIN set → shows "Enter your PIN" form (with server-driven lockout timer)
 *   • On success, stashes the returned unlock_token in sessionStorage and calls onUnlocked()
 *
 * The gate is intentionally lightweight — it only blocks the entrance into
 * the Add Book flow. All other daily-practice features remain unrestricted.
 */
export default function PinGateModal({ onUnlocked, onCancel, context = 'add-book' }) {
  // Iter-51.2: `context` tunes the copy so the modal doesn't always say
  // "add a new book". Supported values:
  //   • add-book  (default) — new book upload + book rename
  //   • rescan               — re-run OCR on an existing page
  //   • debug                — open the 10-tap debug panel
  const COPY = {
    'add-book': {
      subtitle: 'Only needed to add a new book',
      cta: 'Unlock Add Book',
      subtitle_set: 'Set a 4-digit PIN so students can\'t add books on their own',
    },
    'rescan': {
      subtitle: 'Only needed to re-scan a page',
      cta: 'Unlock Re-scan',
      subtitle_set: 'Set a 4-digit PIN so students can\'t re-scan pages on their own',
    },
    'debug': {
      subtitle: 'Only needed to open the debug panel',
      cta: 'Unlock Debug',
      subtitle_set: 'Set a 4-digit PIN so students can\'t open the debug panel on their own',
    },
    'child-override': {
      subtitle: 'Parent/Guardian PIN — override this profile\'s child PIN',
      cta: 'Unlock override',
      subtitle_set: 'Set a 4-digit Parent/Guardian PIN first',
    },
  }[context] || {
    subtitle: 'Only needed for parent/guardian actions',
    cta: 'Unlock',
    subtitle_set: 'Set a 4-digit parent/guardian PIN',
  };
  const [phase, setPhase] = useState('loading'); // loading | set | verify | locked
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [error, setError] = useState('');
  const [lockedUntil, setLockedUntil] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/parent/pin/status');
        if (data.locked_until_seconds > 0) {
          setLockedUntil(data.locked_until_seconds);
          setPhase('locked');
        } else {
          setPhase(data.has_pin ? 'verify' : 'set');
        }
      } catch {
        setError('Could not check PIN status. Please try again.');
      }
    })();
  }, []);

  // Countdown timer for lockout.
  useEffect(() => {
    if (lockedUntil <= 0) return;
    const id = setInterval(() => {
      setLockedUntil((s) => {
        const next = s - 1;
        if (next <= 0) { setPhase('verify'); setError(''); return 0; }
        return next;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [lockedUntil]);

  const validate = (p) => PIN_REGEX.test(p);

  const handleSet = async () => {
    setError('');
    if (!validate(pin)) return setError('PIN must be 4-8 characters (letters, numbers, or symbols).');
    if (pin !== pin2) return setError('Both PINs must match.');
    setBusy(true);
    try {
      await api.post('/parent/pin/set', { pin });
      // Immediately verify to obtain an unlock token so the user isn't prompted twice.
      const { data } = await api.post('/parent/pin/verify', { pin });
      if (data.ok) { setPinUnlockToken(data.unlock_token); onUnlocked(); return; }
      setError('Something went wrong. Please try again.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save PIN.');
    } finally { setBusy(false); }
  };

  const handleVerify = async () => {
    setError('');
    if (!pin) return setError('Enter your PIN.');
    setBusy(true);
    try {
      const { data } = await api.post('/parent/pin/verify', { pin });
      if (data.ok) { setPinUnlockToken(data.unlock_token); onUnlocked(); return; }
      if (data.locked_until_seconds > 0) {
        setLockedUntil(data.locked_until_seconds); setPhase('locked');
      } else {
        setError(`Wrong PIN. ${3 - (data.attempts_used || 0)} tries left before a short lockout.`);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not verify.');
    } finally { setPin(''); setBusy(false); }
  };

  const fmt = (s) => {
    const m = Math.floor(s / 60); const r = s % 60;
    return m ? `${m}m ${r}s` : `${r}s`;
  };

  return (
    <div data-testid="pin-gate-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl w-full max-w-sm p-6 relative">
        <button data-testid="pin-gate-close" onClick={onCancel}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-700"><X size={20}/></button>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-2xl bg-[#FF6B1A] text-white flex items-center justify-center">
            <ShieldCheck size={22} strokeWidth={2.8}/>
          </div>
          <div>
            <div className="text-lg font-extrabold text-[#2B2140]">Parent / Guardian check</div>
            <div className="text-xs text-slate-500">{COPY.subtitle}</div>
          </div>
        </div>

        {phase === 'loading' && <div className="text-slate-500 text-sm py-6">Checking…</div>}

        {phase === 'set' && (
          <>
            <p className="text-sm text-slate-600 my-3">
              Set a 4-8 character PIN (letters, numbers, or symbols). {COPY.subtitle_set}.
            </p>
            <PinInput testId="pin-set-input-1" autoFocus value={pin} onChange={setPin} placeholder="New PIN"/>
            <PinInput testId="pin-set-input-2" value={pin2} onChange={setPin2} placeholder="Confirm New PIN" onEnter={handleSet}/>
            {error && <div className="text-sm text-[#E8435C] font-bold mb-2">{error}</div>}
            <button data-testid="pin-set-submit" onClick={handleSet} disabled={busy}
              className="pushable pushable-primary w-full bg-[#FF6B1A] text-white font-extrabold px-6 py-3 rounded-full disabled:opacity-50">
              Save PIN & continue
            </button>
          </>
        )}

        {phase === 'verify' && (
          <>
            <p className="text-sm text-slate-600 my-3">Enter your PIN to continue.</p>
            <PinInput testId="pin-verify-input" autoFocus value={pin} onChange={setPin} placeholder="PIN" onEnter={handleVerify}/>
            {error && <div className="text-sm text-[#E8435C] font-bold mb-2">{error}</div>}
            <button data-testid="pin-verify-submit" onClick={handleVerify} disabled={busy}
              className="pushable pushable-primary w-full bg-[#FF6B1A] text-white font-extrabold px-6 py-3 rounded-full disabled:opacity-50">
              {COPY.cta}
            </button>
            <p className="text-xs text-slate-500 text-center mt-3">
              Forgot? Sign out from Profile and sign in again to reset it.
            </p>
          </>
        )}

        {phase === 'locked' && (
          <>
            <div className="my-3 py-6 text-center">
              <Lock size={36} strokeWidth={2.5} className="mx-auto text-[#E8435C] mb-3"/>
              <div className="text-lg font-extrabold text-[#2B2140]">Too many wrong tries</div>
              <div className="text-sm text-slate-600 mt-1">
                Try again in <span className="font-extrabold">{fmt(lockedUntil)}</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
